/**
 * The bubble surface (05 §6.4): a clear, glossy sphere. Nearly transparent where it faces the
 * camera and gathering colour toward its rim (Fresnel), with a faint thin-film shimmer at the
 * edge, a bright key highlight up and to the left and a small one low on the right - so a
 * trapped monster reads clearly inside it.
 *
 * Unlike every other scene material it is alpha-blended (the owner asked for better bubbles
 * and a less pixelated game, 2026-09-23). It still writes the second render target the post
 * pass reads, but with alpha 0, so blending leaves the normal/outline buffer untouched, and it
 * writes no depth: outlines and fog see what is behind it. Depth-tested with a bias between the
 * blocks' and the figures', so a dragon or a monster passes in front of a bubble - on the C64,
 * hardware sprites were drawn over the software-drawn bubbles - while a trapped monster, drawn
 * with a smaller bias, stays inside it.
 */

import { Color, GLSL3, NormalBlending, ShaderMaterial } from 'three';
import { sharedUniforms } from './toon';

/** Depth bias of bubbles, in tiles: in front of blocks (0), behind figures (3), over trapped ones (1.5). */
export const BUBBLE_DEPTH_BIAS = 2.5;

const VERT = /* glsl */ `
uniform float uDepthBias;
out vec3 vNormal;
void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_Position.z += projectionMatrix[2][2] * uDepthBias;
}
`;

const FRAG = /* glsl */ `
layout(location = 0) out highp vec4 outColor;
layout(location = 1) out highp vec4 gNormalId;
uniform vec3 uTint;
uniform vec3 uRim;
uniform float uTime;
uniform float uShape;                          // colour aid: 1 = the thick, broken rim of a bubble about to burst
uniform float uBody;                           // how much colour the clear middle holds
in vec3 vNormal;
void main() {
  vec3 n = normalize(vNormal);
  vec3 v = vec3(0.0, 0.0, 1.0);                 // orthographic: one view direction
  float x = 1.0 - clamp(dot(n, v), 0.0, 1.0);   // 0 facing the camera, 1 at the silhouette
  // Thin film: a hue that slides with the angle and turns slowly with time.
  vec3 film = 0.5 + 0.5 * cos(6.2831853 * (vec3(0.0, 0.33, 0.67) + x * 1.4 + n.y * 0.25 + uTime * 0.04));
  // Body: a light tint; then a bright rim; then a crisp darker line at the very edge.
  float rim = smoothstep(0.45, 0.82, x);
  float edge = smoothstep(0.88, 0.96, x);
  vec3 col = mix(uTint, uRim, rim);
  col = mix(col, col * 0.6 + film * 0.5, rim * (1.0 - edge) * 0.4);
  col = mix(col, uTint * 0.45, edge);
  float alpha = mix(uBody, 0.85, rim);
  alpha = mix(alpha, 1.0, edge);
  if (uShape > 0.5) {
    // The outer third of the radius becomes a solid rim broken into eight dashes, dark and bright:
    // a bubble about to burst reads by its shape as well as its colour.
    float band = step(0.3, x);
    float dash = step(0.5, fract(atan(n.y, n.x) / 6.2831853 * 8.0));
    col = mix(col, mix(uTint * 0.35, uRim, dash), band);
    alpha = mix(alpha, 1.0, band);
  }
  // Gloss: a broad soft window up and to the left, a hard spark inside it, a small one low right.
  vec3 key = normalize(vec3(-0.5, 0.62, 0.6));
  float window = smoothstep(0.8, 0.92, dot(n, key)) * 0.55;
  vec3 r = reflect(-v, n);
  float spark = pow(max(dot(r, key), 0.0), 90.0);
  float fill = pow(max(dot(r, normalize(vec3(0.55, -0.5, 0.67))), 0.0), 140.0);
  float shine = clamp(window + spark * 1.6 + fill * 0.9, 0.0, 1.0);
  col = mix(col, vec3(1.0), shine);
  alpha = max(alpha, shine);
  outColor = vec4(col, alpha);
  gNormalId = vec4(0.5, 0.5, 1.0, 0.0);         // alpha 0: blending keeps what is already there
}
`;

export interface GlassBubbleOptions {
  /** The bubble's own colour, saturated: faint through the middle, darker at the very edge (linear). */
  tint: Color;
  /** The bright colour the rim gathers to (linear). */
  rim: Color;
  depthBias?: number;
  /** The colour aid's shape (08 §4): a thick rim broken into dashes. Same program, so nothing compiles. */
  brokenRim?: boolean;
  /** How much colour the clear middle holds, if not the shared `glassBody` (water keeps its own). */
  body?: number;
}

/**
 * How much colour every bubble's clear middle holds, shared by all glass (the game's, the title's): the
 * 1987 look sets it to 0 (GameView), because the faint tint there would lock, under full dither, to a
 * dark colour of the sixteen instead of the black behind it.
 */
export const glassBody = { value: 0.05 };

export function createGlassBubbleMaterial(opts: GlassBubbleOptions): ShaderMaterial {
  return new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    blending: NormalBlending,
    depthWrite: false,
    uniforms: {
      uTint: { value: opts.tint.clone() },
      uRim: { value: opts.rim.clone() },
      uDepthBias: { value: opts.depthBias ?? BUBBLE_DEPTH_BIAS },
      uTime: sharedUniforms.uTime,
      uShape: { value: opts.brokenRim === true ? 1 : 0 },
      uBody: opts.body === undefined ? glassBody : { value: opts.body },
    },
  });
}
